<template>
  <v-container>
    <div class="h-100">
      <v-row class="d-flex">
        <v-col class="pa-0 d-flex align-center justify-start">
          <h1>
            {{ t('compoundExtractsPage.title', [extractTypeTranslation]) }}
          </h1>
        </v-col>
        <v-col class="pa-0 d-flex align-center justify-end">
          <v-btn icon size="small" variant="flat" class="ml-2" @click="goBack">
            <v-icon> mdi-close-circle </v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <v-row v-if="isLoading" class="d-flex h-100 flex-column">
        <progress-bar :message="$t('general.loading')" />
      </v-row>
      <div v-else class="compound-extracts__container h-100">
        <v-row class="ma-0 pb-2">
          <v-col class="pa-0 d-flex align-center justify-start">
            <h3 class="label-input mb-0 mr-2 text-no-wrap">
              {{ tkey('generatePage.extractName') }}:
            </h3>
            <v-text-field
              v-bind="$attrs"
              ref="vTextField"
              v-model="extractName"
              class="rtls-text-field rtls-text-field--grey mr-4"
              hide-details
              single-line
              flat
              density="compact"
              max-width="250"
            />

            <h3 class="label-input mb-0 mr-2 text-no-wrap">{{ tkey('generatePage.scope') }}:</h3>
            <CompoundExtractsSelect @update:scenarios="resetData" />
          </v-col>
        </v-row>
        <AgGridVue
          animate-rows
          dense
          class="h-100 w-100 pb-2"
          :row-data="rowData"
          :column-defs="columnDefs"
          :grid-options="gridOptions"
          :master-detail="true"
          :keep-detail-rows="true"
          :detail-cell-renderer-params="detailCellRendererParams"
          @grid-ready="onGridReady"
        />
        <v-row style="margin-bottom: 1.5rem">
          <v-spacer />
          <v-col class="text-right d-flex align-center justify-end">
            <v-btn
              variant="flat"
              max-height="28"
              action
              :disabled="isGenerateButtonDisabled"
              @click="generate"
            >
              {{ t('actions.generate') }}
            </v-btn>
            <ErrorTriangle
              v-if="isGenerateButtonDisabled"
              data-testid="errorTriangleGenerate"
              :arrow="false"
              class="ml-2"
              :errors="getGenerateButtonErrorMessages"
            />
          </v-col>
        </v-row>
      </div>
    </div>
  </v-container>
</template>

<script>
import { computed, ref, toRaw } from 'vue';
import {
  get,
  size,
  reduce,
  uniq,
  map,
  flatMap,
  sortBy,
  uniqBy,
  concat,
  includes,
  filter,
  find,
  isEqual,
  isEmpty,
  isNull,
  keys,
  mapValues,
  forEach,
  some,
  trim,
  set,
} from 'lodash';
import menuLevel from '@enums/reporting-data-level';
import chainLevels from '@enums/chain-levels';
import extractTypes from '@enums/extract-types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useRoute, useRouter, onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router';
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useContextStore } from '@/stores/context';
import { useBundlesStore } from '@/stores/bundles';
import { useCompoundExtractStore } from '@/stores/compound-extract';
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
import { useWorkpackageScenarioSelectStore } from '@/stores/workpackage-scenario-select';
import { useInternationalization } from '@/composables/use-internationalization';
import { useFeature } from '@/composables/use-feature';
import { useCompoundExtract } from '@/composables/use-compound-extract';
import { isEmbedded } from '@/webshell-embed/embedded';
import { reportTabTitleDetail } from '@/webshell-embed/tab-title';
import AgGridCellRendererSelect from '@/components/AgGridCellRendererSelect.vue';
import AgGridIcon from '@/components/AgGridIcon.vue';
import AgGridIconRichSelect from '@/components/AgGridIconRichSelect.vue';

dayjs.extend(utc);

export default {
  components: {
    /* eslint-disable vue/no-unused-components */
    AgGridCellRendererSelect,
    AgGridIcon,
    AgGridIconRichSelect,
  },

  setup() {
    const workpackagesStore = useWorkpackagesStore();
    const compoundExtractStore = useCompoundExtractStore();
    const assortmentCanvasStore = useAssortmentCanvasStore();
    const contextStore = useContextStore();
    const bundlesStore = useBundlesStore();
    const workpackageScenarioSelectStore = useWorkpackageScenarioSelectStore();
    const route = useRoute();
    const router = useRouter();

    const { t, tkey } = useInternationalization('compoundExtractsPage');
    const { bundlesEnabled, enableFinalCheckpoints } = useFeature();
    const { formatExtractType, isSpaceTest } = useCompoundExtract();

    const noBundle = { _id: null, name: t('reportingPage.noBundle') };
    const extractName = ref('');
    const isLoading = ref(false);
    const rowData = ref([]);

    const isFinal = params => {
      return assortmentCanvasStore.isFinal(params);
    };

    const bundleIds = computed(() => {
      const bundleQueryIds = route.query.bundleIds;
      if (!bundleQueryIds) return isNull(bundleQueryIds) ? [null] : [];
      return concat([], bundleQueryIds);
    });

    const workpackageIds = computed(() => {
      const workpackageQueryIds = route.query.workpackageIds || [];
      return concat([], workpackageQueryIds);
    });

    const extractType = computed(() => {
      return route.meta.type;
    });

    const resetWPData = async () => {
      // get last items first, then fetch scenarios
      await compoundExtractStore.loadWorkpackagesData();

      const wasBundlesChanged = bundlesEnabled
        ? !isEqual(workpackageScenarioSelectStore.selectedBundles, bundleIds.value)
        : false;
      const wasWorkpackagesChanged = !isEqual(
        workpackageScenarioSelectStore.selectedWorkpackages,
        workpackageIds.value
      );
      if (wasBundlesChanged || wasWorkpackagesChanged) {
        const newParams = {};
        if (wasBundlesChanged) newParams.bundleIds = bundleIds.value;
        if (wasWorkpackagesChanged) newParams.workpackageIds = workpackageIds.value;
      }
    };

    const resetBundlesSelected = bundleIdsParam => {
      const bIds = new Set(bundleIdsParam);
      const bundles = bIds.size
        ? filter([...bundlesStore.bundles, noBundle], b => bIds.has(b._id))
        : [];

      workpackageScenarioSelectStore.setSelectedBundles(bundles);
    };

    const resetWorkpackagesSelected = workpackageIdsParam => {
      const wpIds = new Set(workpackageIdsParam);
      const workpackages = wpIds.size
        ? filter(workpackagesStore.workpackages, w => wpIds.has(w._id))
        : [];
      workpackageScenarioSelectStore.setSelectedWorkpackages(workpackages);
    };

    const resetSelected = (selections, loadWPData = false) => {
      const { bundleIds: bundleIdsParam, workpackageIds: workpackageIdsParam } = selections;

      if (bundleIdsParam) resetBundlesSelected(bundleIdsParam);
      if (workpackageIdsParam) resetWorkpackagesSelected(workpackageIdsParam);

      if (!loadWPData) return;
      return resetWPData();
    };

    const convertWpChainToExtractChain = wpChainValue => {
      // The chain values used in the wp setup are different to the ones in extract
      // We convert these here for compatibility
      // The AB conversion step should be removed by AOV3-7678
      // However, CFC should map back to PR once leaving the tool.

      const mappedValue = chainLevels.wpToExtractChainMapping[wpChainValue];

      return mappedValue || wpChainValue;
    };

    const resetData = async () => {
      rowData.value = [];

      let scenarioData = [];
      const bundles = [];

      const bundleWorkpackages = flatMap(workpackageScenarioSelectStore.selectedBundles, b =>
        get(workpackagesStore.workpackagesByBundle, b._id, [])
      );

      const wps = uniqBy(
        [
          ...workpackageScenarioSelectStore.selectedWorkpackages,
          ...(bundlesEnabled ? bundleWorkpackages : []),
        ],
        '_id'
      );

      const workpackageData = reduce(
        sortBy(wps, 'creationDate'),
        (acc, w) => {
          // Ensures that all workpackages not assigned to a bundle have the bundleId key
          if (bundlesEnabled && !w.bundleId) w.bundleId = null;
          // bundleIds are required so that scenarios appear in the correct position in the menu
          const wpScenarios = map(
            workpackageScenarioSelectStore.scenariosByWorkpackage[w._id],
            s => ({
              ...s,
              bundleId: w.bundleId,
            })
          );
          // Workpackage not included if it does not have scenarios
          if (!size(wpScenarios)) return acc;

          scenarioData = scenarioData.concat(wpScenarios);
          if (bundlesEnabled) {
            const bundle = bundlesStore.bundlesById[w.bundleId] || noBundle;
            bundles.push({ ...bundle, menuLevel: menuLevel.bundle });
          }

          // Prefill workpackage's level
          const specificExtractOptions = chainLevels[`${extractType.value}ChainLevels`];

          // We use two different chain enums in setup and in existing extracts
          // This results in different values for some chains, namely AB
          // This checks both and AOV3-7678 will resolve the disparity.
          forEach(specificExtractOptions, (value, chain) => {
            const hasSavedSettingsFromExtract = includes(
              compoundExtractStore.selectedExtract.settings.workpackageLevelMap[chain],
              w._id
            );

            const wpChain = convertWpChainToExtractChain(w.selectedChain);

            const hasSavedChainFromSetup = wpChain === chain;

            if (hasSavedSettingsFromExtract) {
              w.workpackageLevel = chain;
            } else if (hasSavedChainFromSetup) {
              // Ensure mapping has been applied
              chain = wpChain;

              w.workpackageLevel = chain;
            }
          });

          w.menuLevel = menuLevel.workpackage;
          w.scenarios = wpScenarios;
          w.checkpoints = filter(compoundExtractStore.checkpoints, { workpackageId: w._id });

          // check if there is any final checkpoint in the data and not have been copied from another extract
          // the reason for it would be that for final checkpoints we automatically select those checkpoints
          // and if they were copied from another extract then this extract could have different checkpoints selected
          // rather than just the final checkpoints
          const extractWasNotCopied = !get(
            compoundExtractStore,
            'selectedExtract.series.copiedFrom',
            null
          );
          const isForecastFinalCheckpoint = c => c.isFinalCheckpoint && c.hasBeenForecast;
          let finalScenarioId = null;

          w.canvas = map(compoundExtractStore.canvases, canvas => {
            // We can have one final checkpoint per canvas
            const canvasFinalCheckpoint = find(
              w.checkpoints,
              checkpoint =>
                isForecastFinalCheckpoint(checkpoint) &&
                extractWasNotCopied &&
                checkpoint.canvasIdCombined === canvas.canvasIdCombined
            );

            if (canvasFinalCheckpoint) {
              compoundExtractStore.addSelectedRows(canvasFinalCheckpoint._id);
              finalScenarioId = canvasFinalCheckpoint.scenarioId;
            }

            return {
              ...canvas,
              // list of checkpoints that will be shown as options
              checkpoints: filter(
                compoundExtractStore.checkpoints,
                checkpoint =>
                  checkpoint.canvasIdCombined === canvas.canvasIdCombined &&
                  checkpoint.hasBeenForecast
              ),
              // selected checkpoint
              checkpoint: canvasFinalCheckpoint || null,
            };
          });

          if (finalScenarioId) {
            w.scenario = find(w.scenarios, scenario => scenario._id === finalScenarioId);
          }

          acc.push(toRaw(w));
          return acc;
        },
        []
      );

      // There is no data to display if there are no scenarios
      if (!size(scenarioData)) return;

      if (enableFinalCheckpoints) {
        // Get final scenarios for workpackages
        await assortmentCanvasStore.fetchFinalScenarios(map(workpackageData, '_id'));
      }

      rowData.value = workpackageData;
    };

    const loadAllWorkpackagesData = async () => {
      isLoading.value = true;
      try {
        const subType = get(compoundExtractStore, 'selectedExtract.series.subType', null);
        const spaceTest = isSpaceTest(subType);
        // even if there are workpackages in state, we need to refetch it to pull this extra info
        await workpackagesStore.fetchWorkpackages({
          shouldGetTemplates: false,
          // We should fetch only final workpackages for non space test extracts,
          // space test extracts can have non final workpackages that they could still be relevant
          shouldGetFinalOnly: !spaceTest,
          pickExtra: ['fillInSelection'],
        });

        const selections = {};

        if (bundlesEnabled) {
          await bundlesStore.fetchBundles({
            pick: ['_id', 'name', 'creationDate', 'createdBy', 'targetLaunchDate'],
          });

          if (!size(workpackageScenarioSelectStore.selectedBundles) && size(bundleIds.value)) {
            selections.bundleIds = bundleIds.value;
          }
        }

        if (
          !size(workpackageScenarioSelectStore.selectedWorkpackages) &&
          size(workpackageIds.value)
        ) {
          // when loading for the first time (copy & paste full link), we take whatever is on the url is
          selections.workpackageIds = workpackageIds.value;
        }

        await resetSelected(selections, true);

        // we do this call at the end as it needs to be done after the workpackages are loaded
        await workpackageScenarioSelectStore.fetchWorkpackageScenarios({
          shouldGetFinalOnly: !spaceTest,
        });

        // finally rebuild the tree
        resetData();
      } finally {
        isLoading.value = false;
      }
    };

    onBeforeRouteLeave(() => {
      const selections = { workpackageIds: [] };
      if (bundlesEnabled) selections.bundleIds = [];
      resetSelected(selections);
    });

    onBeforeRouteUpdate((to, from) => {
      const selections = {};

      if (bundlesEnabled) {
        const bundleIdsTo = get(to, 'query.bundleIds', []);
        const previousBundleIds = concat([], from.query.bundleIds || []);

        if (!isEqual(bundleIdsTo, previousBundleIds)) {
          selections.bundleIds = bundleIdsTo;
        }
      }
      const workpackageIdsTo = get(to, 'query.workpackageIds', []);
      const previousWorkpackageIds = concat([], from.query.workpackageIds || []);

      if (!isEqual(workpackageIdsTo, previousWorkpackageIds)) {
        selections.workpackageIds = workpackageIdsTo;
      }
      // when selected bundles / workpackages are the same, we assume the user has changed filters,
      // which don't trigger more updates
      if (isEmpty(selections)) return false;

      // on selection change, reset all report section data
      resetSelected(selections, true);
    });

    return {
      t,
      tkey,
      route,
      router,
      compoundExtractStore,
      workpackageScenarioSelectStore,
      workpackagesStore,
      assortmentCanvasStore,
      bundlesStore,
      bundlesEnabled,
      enableFinalCheckpoints,
      clientConfig: contextStore.clientConfig,
      getDateFormats: contextStore.getDateFormats,
      extractName,
      isLoading,
      rowData,
      isFinal,
      extractType,
      resetData,
      loadAllWorkpackagesData,
      convertWpChainToExtractChain,
      formatExtractType,
      isSpaceTest,
    };
  },

  data() {
    return {
      gridApi: null,
      // tracks that every workpackage that appears has a checkpoint selected
      isCheckpointSelected: false,
      // tracks if user selects a CFC wp without a PR wp for HASU
      onlyCFCCheckpointsSelectedWithoutPR: false,
      // tracks that every store class has only either clustered or unclustered selected cps
      hasMixedClusteredAndUnclusteredSelections: false,
      gridOptions: {
        columnMenu: 'legacy',
        animateRows: true,
        headerHeight: 30,
        rowHeight: 30,
        suppressContextMenu: true,
        masterDetail: true,
        detailRowAutoHeight: true,
        suppressGroupRowsSticky: true,
        defaultColDef: {
          filter: false,
          sortable: false,
          menuTabs: [],
          suppressMovable: true,
          tooltipShowDelay: 0,
          flex: 1,
          suppressHeaderMenuButton: true, // hide header menu by
          resizable: false,
          headerClass: 'pa-0',
        },
        onRowGroupOpened: params => {
          params.api.redrawRows({ rowNodes: [params.node] });
        },
        isRowMaster: dataItem => (dataItem.scenarios ? dataItem.scenarios.length > 0 : false),
        autoGroupColumnDef: {
          headerName: this.tkey('generatePage.workpackageName'),
          field: 'name',
          cellRenderer: 'agGroupCellRenderer',
          hide: false,
        },
        onFirstDataRendered: () => {
          // Since we have final checkpoints being applied automatically
          // once the first data is rendered we should already validate the selecitons
          this.validateGridSelections();
        },
      },
      noBundle: { _id: null, name: this.t('reportingPage.noBundle') },
      selectedView: 'all',
      searchString: '',
      filterModel: null,
      detailCellRendererParams: null,
    };
  },

  computed: {
    extractSeriesId() {
      return this.route.params.extractSeriesId;
    },

    chainLevelOptions() {
      const options = chainLevels[`${this.extractType}ChainLevels`];
      return mapValues(options, name => ({ name }));
    },

    columnDefs() {
      return [
        {
          headerName: this.tkey('generatePage.workpackageName'),
          field: 'name',
          cellRenderer: 'agGroupCellRenderer',
        },
        {
          headerName: `${this.t('general.select')} ${this.t(
            'reportingPage.sections.product.table.chain'
          )}`,
          field: 'workpackageLevel',
          colId: 'workpackageLevel',
          cellEditor: 'agRichSelectCellEditor',
          editable: true,
          cellEditorParams: {
            values: keys(this.chainLevelOptions),
            allowTyping: true,
            filterList: true,
            highlightMatch: true,
          },
          cellClass: 'underlined-overridden',
        },
        {
          headerName: `${this.t('general.select')} ${this.t('entities.scenario')}`,
          field: 'scenario',
          colId: 'scenario',
          cellEditor: 'agRichSelectCellEditor',
          editable: true,
          cellEditorParams: params => {
            return {
              values: map(
                params.data.scenarios,
                // Here we set some props needed for AgGridIconRichSelect
                scenario => ({
                  ...scenario,
                  showIcon: this.assortmentCanvasStore.isScenarioFinal(scenario._id),
                  iconName: 'mdi-star',
                  textValue: get(scenario, 'name', ''),
                  addSpaceToTextValue: true,
                })
              ),
              allowTyping: true,
              filterList: true,
              highlightMatch: true,
              formatValue: scenario => {
                return scenario ? scenario.name : '';
              },
              valueListMaxHeight: 100,
              cellRenderer: AgGridIconRichSelect,
            };
          },
          cellRenderer: AgGridIconRichSelect,
          cellClass: 'underlined-overridden',
          valueGetter: params => {
            if (!params.data.scenario) return null;

            return {
              ...params.data.scenario,
              showIcon: this.assortmentCanvasStore.isScenarioFinal(
                get(params.data, 'scenario._id', false)
              ),
              iconName: 'mdi-star',
              textValue: get(params.data, 'scenario.name', ''),
              addSpaceToTextValue: false,
            };
          },
          onCellValueChanged: params => {
            params.api.applyTransaction({ update: [params.data] });
          },
        },
        {
          headerName: this.tkey('generatePage.completed'),
          headerClass: 'justify-center',
          field: 'completed',
          cellClass: 'd-flex align-center justify-center',
          cellRenderer: AgGridIcon,
          cellRendererParams: params => ({
            value: params.value ? 'mdi-check-circle' : '',
            size: params.value ? '20px' : 0,
            color: 'success',
          }),
          width: 50,
          valueGetter: params => {
            const wpCanvas = get(params.data, 'canvas', []);
            const hasAnySelectedCheckpoint = some(wpCanvas, canvas => !!canvas.checkpoint);
            return hasAnySelectedCheckpoint;
          },
        },
      ];
    },

    detailGridOptionsColDefs() {
      const columns = [
        {
          headerName: `${this.t('entities.canvas')}`,
          field: 'name',
          colId: 'name',
          maxWidth: 250,
          cellRenderer: AgGridIconRichSelect,
          valueFormatter: params => this.compoundExtractStore.getCanvasName(params.data),
          valueGetter: params => {
            return {
              ...params.data,
              showIcon: get(params.data, 'isFinalCheckpoint', false),
              iconName: 'mdi-star',
              textValue: this.compoundExtractStore.getCanvasName(params.data),
            };
          },
        },
        {
          headerName: this.t('entities.checkpoint'),
          field: 'checkpoint',
          colId: 'checkpoint',
          editable: true,
          maxWidth: 250,
          cellEditor: 'agRichSelectCellEditor',
          cellEditorParams: params => {
            return {
              values: map(
                params.data.checkpoints,
                // Here we set some props needed for AgGridIconRichSelect
                checkpoint => ({
                  ...checkpoint,
                  showIcon: get(checkpoint, 'isFinalCheckpoint', false),
                  iconName: 'mdi-star',
                  textValue: get(checkpoint, 'checkpointMeta.name', ''),
                  addSpaceToTextValue: true,
                })
              ),
              allowTyping: true,
              filterList: true,
              highlightMatch: true,
              cellRenderer: AgGridIconRichSelect,
              valueListMaxHeight: 100,
            };
          },
          cellClass: 'underlined-overridden',
          cellRenderer: AgGridIconRichSelect,
          valueFormatter: params => (params.value ? params.value.checkpointMeta.name : ''),
          valueGetter: params => {
            if (!params.data.checkpoint) return null;

            return {
              ...params.data.checkpoint,
              showIcon: get(params.data.checkpoint, 'isFinalCheckpoint', false),
              iconName: 'mdi-star',
              textValue: get(params.data.checkpoint, 'checkpointMeta.name', false),
              addSpaceToTextValue: false,
            };
          },
          onCellValueChanged: params => {
            if (params.newValue?._id === params.oldValue?._id) {
              return;
            }

            if (params.newValue?._id) {
              this.compoundExtractStore.addSelectedRows(params.newValue._id);
            }

            if (params.oldValue?._id) {
              this.compoundExtractStore.removeSelectedRows(params.oldValue._id);
            }
            this.gridApi.refreshCells({ columns: ['completed'], force: true });
          },
        },
      ];

      return columns;
    },

    hasExtractName() {
      return !!trim(this.extractName);
    },

    isGenerateButtonDisabled() {
      return (
        !this.isCheckpointSelected ||
        !this.hasExtractName ||
        this.onlyCFCCheckpointsSelectedWithoutPR ||
        this.hasMixedClusteredAndUnclusteredSelections
      );
    },

    getGenerateButtonErrorMessages() {
      // Note that every value must be negated for the messages to appear.
      return {
        [this.tkey('generatePage.cannotGenerateExtractWithOnlyCFCCheckpoints')]:
          !this.onlyCFCCheckpointsSelectedWithoutPR,
        [this.t('general.cannotMixUnclusteredWithClustered')]:
          !this.hasMixedClusteredAndUnclusteredSelections,
        [this.tkey('generatePage.missingExtractName')]: this.hasExtractName,
        [this.tkey('generatePage.missingCheckpointSelection')]: this.isCheckpointSelected,
      };
    },

    extractTypeTranslation() {
      const subType = get(this.compoundExtractStore, 'selectedExtract.series.subType', null);
      return this.formatExtractType({ t: this.t, type: this.extractType, subType });
    },
  },

  watch: {
    '$route.query': {
      handler(search) {
        if (size(search)) {
          this.resetLocalState();
        }
      },
      deep: true,
    },

    'compoundExtractStore.selectedRows': {
      handler(newValue) {
        if (!newValue || !this.gridApi) {
          this.isCheckpointSelected = false;
          return;
        }
        this.validateGridSelections();
      },
      immediate: true,
      deep: true,
    },

    hasExtractName() {
      this.validateGridSelections();
    },
  },

  async created() {
    if (!this.compoundExtractStore.selectedExtract) {
      await this.compoundExtractStore.fetchExtractSeriesById(this.extractSeriesId);
    }

    await this.loadAllWorkpackagesData();
    this.resetData();
    this.detailCellRendererParams = {
      detailGridOptions: {
        columnDefs: this.detailGridOptionsColDefs,
        defaultColDef: {
          filter: false,
          sortable: false,
          menuTabs: [],
          suppressMovable: true,
          flex: 1,
          suppressHeaderMenuButton: true, // hide header menu by
          resizable: false,
        },
        rowHeight: 30,
        headerHeight: 40,
        statusBar: { statusPanels: [] },
      },
      getDetailRowData: params => {
        if (!params.data.scenario) {
          return params.successCallback([]);
        }
        // render only the canvas for the selected scenario
        const data = filter(
          params.data.canvas,
          canvas => canvas.scenarioId === params.data.scenario._id
        );
        params.successCallback(data);
      },
    };
    this.setExtractTypeTranslationTitle();
  },

  methods: {
    async resetLocalState() {
      this.isCheckpointSelected = false;
      this.compoundExtractStore.setSelectedRows([]);
      await this.loadAllWorkpackagesData().finally(() => {
        this.resetData();
      });
    },

    handleCheckboxChange(params) {
      const { api, data, node } = params;

      if (!includes(this.compoundExtractStore.selectedRows, data._id)) {
        // remove all checkpoints that are in the same cluster before add the new checkpoint
        const checkpointsIdsInCluster = node.parent.childrenAfterGroup.map(value => value.data._id);

        // Remove any existing checkpoints in the same cluster
        checkpointsIdsInCluster.forEach(id => {
          if (includes(this.compoundExtractStore.selectedRows, id)) {
            this.compoundExtractStore.removeSelectedRows(id);
          }
        });

        // Add the new checkpoint
        this.compoundExtractStore.addSelectedRows(data._id);
        api.redrawRows([]);
        return;
      }

      // Remove the checkpoint if it was already selected
      this.compoundExtractStore.removeSelectedRows(data._id);
      api.redrawRows([]);
    },

    onGridReady(params) {
      this.gridApi = params.api;
      this.gridApi.sizeColumnsToFit();
    },

    getFormattedName(params, fromSource, path = 'name') {
      const dbEntry = fromSource[params.value];
      return get(dbEntry, path, this.t('general.select'));
    },

    getSeriesData() {
      const seriesData = {
        bundleIds: [],
        workpackageIds: [],
        scenarioIds: [],
        checkpointIds: [],
        assortmentGroups: [],
      };

      const settingsData = {
        workpackageLevelMap: { ...mapValues(this.chainLevelOptions, () => []) },
      };

      this.gridApi.forEachNode(node => {
        // Since we use Detail Grid we use the canvas list as the rows
        // and each canvas would have a selected checkpoint
        const wpCanvas = get(node.data, 'canvas', []);
        const hasAnySelectedCheckpoint = some(wpCanvas, canvas => !!canvas.checkpoint);

        // If the workpackage have canvas we loop through it to find selected checkpoints per canvas
        // Check if the workpackage have selected checkpoints so we can loop through it to find selected checkpoints per canvas
        if (hasAnySelectedCheckpoint) {
          forEach(wpCanvas, canvas => {
            const selectedCheckpoint = canvas.checkpoint;
            if (
              selectedCheckpoint &&
              includes(this.compoundExtractStore.selectedRows, selectedCheckpoint._id)
            ) {
              seriesData.scenarioIds.push(selectedCheckpoint.scenarioId);
              seriesData.checkpointIds.push(selectedCheckpoint._id);
              seriesData.bundleIds.push(selectedCheckpoint.bundleId);
              seriesData.workpackageIds.push(selectedCheckpoint.workpackageId);

              // We check if the workpackage level was already added and if not add it
              // and avoid duplicates
              const workpackageLevelMap = this.getWorkpackageLevelMap(node);
              const workpackageLevels =
                settingsData.workpackageLevelMap[workpackageLevelMap.workpackageLevel];
              if (!includes(workpackageLevels, workpackageLevelMap.workpackageId)) {
                workpackageLevels.push(workpackageLevelMap.workpackageId);
              }
            }
          });
        }
      });

      const removeDuplicatesAndUndefined = arr => uniq(filter(arr));

      return {
        series: mapValues(seriesData, removeDuplicatesAndUndefined),
        settings: settingsData,
      };
    },

    getWorkpackageLevelMap(node) {
      if (!node.data) return;

      return {
        workpackageSelectedChain: node.data.selectedChain,
        workpackageLevel: node.data.workpackageLevel,
        workpackageId: node.data._id,
      };
    },

    async generate() {
      await this.compoundExtractStore.updateExtract(this.getSeriesData());
      await this.compoundExtractStore.generateCompoundExtract({
        filenameOnDownload:
          this.extractName ||
          this.t('extract.reports.defaultExtractFileName', [
            dayjs().format(this.getDateFormats.longWithTime),
          ]),
        extractType: this.extractType,
        extractDatetime: dayjs.utc().toDate(),
        fileId: null,
      });
      // keep on same path
      this.router.push({ path: '/extracts' });
    },

    goBack() {
      this.router.push({ path: '/extracts' });
    },

    validateGridSelections() {
      // the chains of each workpackage that has a checkpoint selected
      const selectedWorkpackageChains = new Set();
      // Capture the cluster stategy per scenario-storeclass selected
      const selectionsPerChainStoreClass = {};

      this.gridApi.forEachNode(node => {
        // Since we use Detail Grid we use the canvas list as the rows
        // and each canvas would have a selected checkpoint
        const wpCanvas = get(node.data, 'canvas', []);
        const hasAnySelectedCheckpoint = some(wpCanvas, canvas => !!canvas.checkpoint);

        if (hasAnySelectedCheckpoint) {
          const workpackageLevelMap = this.getWorkpackageLevelMap(node);
          selectedWorkpackageChains.add(workpackageLevelMap.workpackageSelectedChain);

          // If the workpackage have canvas we loop through it to find selected checkpoints per canvas
          forEach(wpCanvas, canvas => {
            const selectedCheckpoint = canvas.checkpoint;
            if (
              selectedCheckpoint &&
              includes(this.compoundExtractStore.selectedRows, selectedCheckpoint._id)
            ) {
              set(
                selectionsPerChainStoreClass,
                [
                  selectedCheckpoint.storeClassId,
                  !selectedCheckpoint.clusterId
                    ? 'hasUnclusteredSelection'
                    : 'hasClusteredSelection',
                ],
                true
              );
            }
          });
        }
      });
      this.isCheckpointSelected = !!this.compoundExtractStore.selectedRows.length;
      this.onlyCFCCheckpointsSelectedWithoutPR =
        this.extractType === extractTypes.HASU &&
        selectedWorkpackageChains.has('CFC') &&
        !selectedWorkpackageChains.has('PR');

      this.hasMixedClusteredAndUnclusteredSelections = some(
        selectionsPerChainStoreClass,
        ({ hasUnclusteredSelection, hasClusteredSelection }) =>
          hasClusteredSelection && hasUnclusteredSelection
      );
    },
    // Titled here rather than from route metadata because the subType only exists once the extract
    // series has been fetched, which `created` awaits. Embedded, the shell owns the document, so the
    // same detail is reported to it instead - ADR-018 - https://github.com/owg-digital/rtls-one-retail/tree/main/frontend/shell/docs/adrs/018-shell-owns-the-tab-title.md
    setExtractTypeTranslationTitle() {
      const subType = get(this.compoundExtractStore, 'selectedExtract.series.subType', null);
      const extractType = this.formatExtractType({ t: this.t, type: this.extractType, subType });
      if (isEmbedded()) {
        reportTabTitleDetail(extractType);
        return;
      }
      document.title = `${this.t('general.appTitle', { fallback: 'Assortment' })} | ${extractType}`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.compound-extracts {
  &__container {
    display: flex;
    flex-direction: column;
  }
}

:deep(.ag-cell-not-inline-editing) {
  &.underlined-overridden {
    border-bottom: 0.2rem solid $assortment-primary-colour !important;
    border-left-width: 0.1rem !important;
    border-right-width: 0.1rem !important;
    padding-left: 0.5rem !important;

    &.ag-cell-focus {
      padding-bottom: 0.1rem !important;
    }
  }
}

:deep(.ag-details-row) {
  padding: 0 !important;
  padding-left: 17px !important;
}

:deep(.ag-details-grid) {
  border-left: 0.2rem solid #eaebeb !important;
}

:deep(.ag-center-cols-viewport) {
  min-height: 30px !important;
}

.label-input {
  font-size: 1.3rem;
}

.step-tab-panel {
  background-color: $assortment-background !important;
}

:deep(.v-field__input) {
  .v-select__selection {
    padding-top: 10px;
  }
}

:deep(.v-field__append-inner) {
  padding: 2px 2px 0 0 !important;
}

:deep(.round-selection) {
  color: $baseline-colour;
  font-family: 'Material Design Icons';
  font-size: 15px;
  position: relative;

  &:before {
    content: '\F043D';
    height: 15px;
    width: 15px;
  }

  &[disabled]:before {
    color: $assortment-disabled-text-button-colour;
  }
}

:deep(.selected-selected):before {
  content: '\F043E';
  height: 15px;
  width: 15px;
}

:deep(.ag-popup > .ag-tooltip) {
  color: $assortment-text-colour !important;
  background-color: $assortment-dialog-background !important;
  border: 1px solid $assortment-text-colour !important;
  min-width: fit-content !important;
}

:deep(.ag-layout-normal > .ag-body-viewport.ag-layout-normal) {
  scrollbar-gutter: stable;
}

:deep(.rtls-text-field) {
  width: 250px;
}

:deep(.rtls-text-field .v-input__slot) {
  height: 28px;
}

:deep(.rtls-text-field .v-text-field__slot) {
  min-height: auto;
  line-height: normal;
  height: 28px;

  input {
    padding-top: 11px;
    padding-left: 5px;
  }
}

:deep(.rtls-text-field--grey .v-input__slot),
:deep(.rtls-text-field--grey .v-select__slot) {
  background: #f2f3f4;
  height: 28px !important;
}

:deep(.rtls-text-field .v-text-field__slot input) {
  padding: 5px !important;
}

:deep(.rtls-text-field .v-field__input) {
  height: 28px;
  padding-top: 0px;
  font-size: 1.3rem;
  padding-bottom: 10px;
  padding-left: 10px;
}

:deep(.rtls-text-field .v-field__field) {
  height: 28px;
  background-color: white;
}

:deep(.rtls-text-field.v-field--focused.v-field--variant-filled .v-field__outline::after),
:deep(.rtls-text-field.v-field--focused.v-field--variant-underlined .v-field__outline::after) {
  border-color: $assortment-primary-colour !important;
}
</style>
